import type { ReactNode } from 'react'
import {
  CheckMarkIcon,
  CloseOutlineIcon as ErrorIcon,
  InfoOutlineIcon as InfoIcon,
  WarningIcon
} from '@mote/assets/icons'
import type { SystemProps } from '@xstyled/styled-components'
import styled from '@xstyled/styled-components'
import type { HTMLXStyledProps } from '../../system'
import { Text } from '../typography/text'

export type AlertType = 'error' | 'warn' | 'success' | 'info'

interface AlertDynamicProps {
  color: SystemProps['color']
  bg: SystemProps['bg']
  icon: JSX.Element
}

function getDynamicProps(type: AlertType): AlertDynamicProps {
  switch (type) {
    case 'warn':
      return {
        icon: <WarningIcon />,
        color: 'warning',
        bg: 'orange.l2'
      }
    case 'success':
      return {
        icon: <CheckMarkIcon />,
        color: 'success',
        bg: 'green.l2'
      }
    case 'info':
      return {
        icon: <InfoIcon />,
        color: 'information',
        bg: 'sky.l2'
      }
    case 'error':
    default:
      return {
        icon: <ErrorIcon />,
        color: 'error',
        bg: 'red.l2'
      }
  }
}

export interface AlertProps extends HTMLXStyledProps<'div'> {
  type?: AlertType
  message: ReactNode
}

export const Alert = (props: AlertProps): JSX.Element => {
  const dynamicProps = getDynamicProps(props.type || 'error')

  return (
    <Root
      {...{
        role: 'alert',
        fontSize: 'md',
        lineHeight: 'md',
        fontWeight: 'normal',
        ...props,
        color: dynamicProps.color,
        bg: dynamicProps.bg
      }}
    >
      {dynamicProps.icon}
      <Text fontSize='inherit' lineHeight='inherit' fontWeight='inherit'>
        {props.message}
      </Text>
    </Root>
  )
}

const Root = styled.divBox`
  min-height: 40px;
  width: 100%;
  padding: 8px 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 2;

  svg {
    flex: 0 0 auto;
  }
`
